import React, { useMemo, useState } from 'react';
import { message } from 'antd';
import { ApprovalWorkflowItemDto, AWStageDto } from '@api/Api';
import {
  useRestartApprovalWorkflow,
  useSendReminderMemberApprovalStage,
  useUpdateApprovalWorkflow
} from '@hooks/queries/approval';
import { ReactComponent as SuccessSvg } from '@assets/icons/success.svg';
import ApprovalPreviewCollapse from '@components/Modals/ApprovalModal/components/ApprovalPreview/components/ApprovalPreviewCollapse';
import { nonNull } from '@helpers/non-null';

const RestartApprovalModal = React.lazy(
  () => import('@components/Modals/ApprovalModal/RestartApprovalModal')
);

type RestartConfirmationValue = {
  visible: boolean;
  stage?: AWStageDto & { stageNumber: number };
};

type AssetModalStagesProps = {
  assetId: string;
  versionId: string;
  approvalWorkflow: ApprovalWorkflowItemDto;
  sendMixpanelEvent: () => void;
  onUpdateApprovalWorkflow: (approvalWorkflow: ApprovalWorkflowItemDto) => void;
};

export default function AssetModalStages({
  approvalWorkflow,
  assetId,
  versionId,
  sendMixpanelEvent,
  onUpdateApprovalWorkflow
}: AssetModalStagesProps) {
  const canManage = approvalWorkflow?.permissions?.manage;
  const currentStageId = approvalWorkflow?.currentStageId;

  const [restartConfirmation, setRestartConfirmation] =
    useState<RestartConfirmationValue>({ visible: false });

  const stages = useMemo(
    () => approvalWorkflow?.stages || [],
    [approvalWorkflow?.stages]
  );

  const asset = useMemo(() => {
    return (approvalWorkflow?.assets || []).find(
      (awAsset) => awAsset.id === assetId && awAsset.versionId === versionId
    );
  }, [assetId, versionId, approvalWorkflow?.assets]);

  const {
    mutateAsync: updateApprovalWorkflow,
    isPending: isLoadingUpdateApprovalWorkflow
  } = useUpdateApprovalWorkflow({});

  const {
    mutateAsync: restartApprovalWorkflow,
    isPending: isLoadingRestartApprovalWorkflow
  } = useRestartApprovalWorkflow({});

  const { mutateAsync: sendReminderMemberApprovalStage } =
    useSendReminderMemberApprovalStage({});

  const onSendReminder = async (stageId: string, email: string) => {
    if (!approvalWorkflow?.id) return;
    sendMixpanelEvent?.();
    message.success({
      content: 'Reminder has been sent!',
      className: 'message-dark-modal',
      icon: <SuccessSvg className="anticon" />
    });
    await sendReminderMemberApprovalStage({
      id: approvalWorkflow.id,
      stageId,
      emails: [email]
    });
  };

  const toggleSkipMemberDecision = async (
    stageId: string,
    email: string,
    decisionSkipped: boolean
  ) => {
    if (isLoadingUpdateApprovalWorkflow) return;
    if (!approvalWorkflow?.id) return;
    if (!asset) return;
    try {
      const {
        data: { approvalWorkflow: resApprovalWorkflow }
      } = await updateApprovalWorkflow({
        id: approvalWorkflow.id,
        stages: {
          update: [
            {
              id: stageId,
              members: {
                update: [
                  {
                    email,
                    decisions: [
                      { decisionSkipped, assetVersionId: asset.versionId }
                    ]
                  }
                ]
              }
            }
          ]
        }
      });
      sendMixpanelEvent?.();
      onUpdateApprovalWorkflow(resApprovalWorkflow);
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Something went wrong');
    }
  };

  const resetMemberDecision = async (stageId: string, email: string) => {
    if (isLoadingUpdateApprovalWorkflow) return;
    if (!approvalWorkflow?.id) return;
    if (!asset?.versionId) return;
    try {
      const {
        data: { approvalWorkflow: resApprovalWorkflow }
      } = await updateApprovalWorkflow({
        id: approvalWorkflow.id,
        stages: {
          update: [
            {
              id: stageId,
              members: {
                update: [
                  {
                    email,
                    decisions: [
                      {
                        assetVersionId: asset.versionId,
                        decision: null
                      }
                    ]
                  }
                ]
              }
            }
          ]
        }
      });
      sendMixpanelEvent?.();
      onUpdateApprovalWorkflow(resApprovalWorkflow);
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Something went wrong');
    }
  };

  const onRestart = async (stageId?: string) => {
    if (isLoadingRestartApprovalWorkflow) return;
    if (!approvalWorkflow?.id) return;
    if (!stageId) return;
    try {
      const {
        data: { approvalWorkflow: resApprovalWorkflow }
      } = await restartApprovalWorkflow({
        id: approvalWorkflow.id,
        stageId
      });
      setRestartConfirmation({ ...restartConfirmation, visible: false });
      sendMixpanelEvent?.();
      onUpdateApprovalWorkflow(resApprovalWorkflow);
      const stage = nonNull(
        approvalWorkflow.stages.find((x) => x.id === stageId)
      );
      message.success({
        key: 'aw-restarted',
        content: `${approvalWorkflow.name} approval routing has been restarted from stage ${stage.name}.`,
        className: 'message-dark-modal',
        icon: <SuccessSvg className="anticon" />,
        duration: 2
      });
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Something went wrong');
    }
  };

  const formattedStages = useMemo(() => {
    const _stages = stages.map((stage, i) => ({
      ...stage,
      stageNumber: i + 1
    }));
    const pendingStages =
      _stages.filter((stage) => {
        const justStartedStage =
          currentStageId === stage.id && stage.status === 'pending';
        if (justStartedStage) return false;
        return stage.status === 'pending';
      }) || [];
    const inProgressStages =
      _stages.filter((stage) => {
        const justStartedStage =
          currentStageId === stage.id && stage.status === 'pending';
        if (justStartedStage) return true;
        return (
          stage.status === 'in_progress' || stage.status === 'changes_required'
        );
      }) || [];
    const completedStages = _stages.filter((stage) => stage.isFinished) || [];
    return [
      {
        key: 'completed',
        stages: completedStages,
        copy: 'COMPLETED STAGES',
        canSkipDecision: false
      },
      {
        key: 'in-progress',
        stages: inProgressStages,
        copy: 'IN PROGRESS STAGES',
        canSkipDecision: canManage
      },
      {
        key: 'pending',
        stages: pendingStages,
        copy: 'PENDING',
        canSkipDecision: canManage
      }
    ];
  }, [stages, currentStageId, canManage]);

  const defaultOpenedStage = useMemo(
    () =>
      stages.find((x) => !x.isFinished)?.id ?? stages[stages.length - 1]?.id,
    [stages]
  );

  return (
    <>
      {formattedStages
        .filter((x) => x.stages.length)
        .map(({ stages, canSkipDecision, copy, key }) => {
          return (
            <ApprovalPreviewCollapse
              key={key}
              defaultOpenedStage={defaultOpenedStage}
              assetVersionId={asset?.versionId || ''}
              currentStageId={approvalWorkflow.currentStageId}
              isShowDecision={true}
              stages={stages}
              copy={copy}
              toggleSkipMemberDecision={
                canSkipDecision ? toggleSkipMemberDecision : undefined
              }
              resetMemberDecision={canManage ? resetMemberDecision : undefined}
              onSendReminder={
                canManage && !approvalWorkflow.isPaused
                  ? onSendReminder
                  : undefined
              }
              onRestart={(stage) => {
                setRestartConfirmation({ stage, visible: true });
              }}
              approvalWorkflow={approvalWorkflow}
            />
          );
        })}
      <RestartApprovalModal
        visible={restartConfirmation.visible}
        stage={restartConfirmation.stage}
        onCancel={() =>
          setRestartConfirmation({ ...restartConfirmation, visible: false })
        }
        onConfirm={() => onRestart(restartConfirmation.stage?.id)}
      />
    </>
  );
}
